import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button, Typography } from '@/primer/components';
import { PageLoadingState } from '@/components/layout';
import { useInstagramCallback } from '@/api';
import { SocialProvider } from '@/api/queries/settings/constants';
import {
  OAUTH_PROVIDER_KEY,
  RETURN_TO_KEY,
  getOAuthProvider,
  getSafeReturnTo,
  PROVIDER_DISPLAY_NAME,
} from '@/utils/oauth';
import type { OAuthStatus } from '@/types';
import { getApiErrorMessage } from '@/utils/apiError';

export function OAuthCallback() {
  const callbackMutation = useInstagramCallback();
  const navigate = useNavigate();
  const [status, setStatus] = useState<OAuthStatus>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const hasRunRef = useRef(false);

  // Read non-destructively during render so StrictMode double-invocation doesn't lose the values.
  // Keys are cleared once the guarded effect commits below.
  const [provider] = useState(() => getOAuthProvider(SocialProvider.INSTAGRAM));
  const [returnTo] = useState(() => getSafeReturnTo());
  const providerName =
    PROVIDER_DISPLAY_NAME[provider as keyof typeof PROVIDER_DISPLAY_NAME] ?? provider;

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    // Clear sessionStorage now that we've committed — safe to do once here.
    sessionStorage.removeItem(OAUTH_PROVIDER_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);

    const searchParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
    const code = searchParams.get('code') ?? hashParams.get('code');
    const error = searchParams.get('error') ?? hashParams.get('error');

    if (error || !code) {
      const msg =
        error === 'access_denied'
          ? `${providerName} login was cancelled.`
          : `No code received from ${providerName}.`;
      setStatus('error');
      setErrorMsg(msg);
      return;
    }

    callbackMutation
      .mutateAsync(code)
      .then(() => {
        setStatus('success');
        navigate({ to: returnTo as string & {} });
      })
      .catch((err: unknown) => {
        setStatus('error');
        setErrorMsg(
          getApiErrorMessage(err, {
            messageMap: {
              'Invalid code': `We couldn't verify your ${providerName} connection. Please go back and try again.`,
            },
            fallback: `We couldn't connect ${providerName} right now. Please go back and try again.`,
          })
        );
      });
    // Intentionally empty deps: this effect reads URL params and sessionStorage
    // at mount time only. Re-running on callbackMutation/navigate changes would
    // duplicate the OAuth exchange on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === 'loading') {
    return <PageLoadingState size="large" label="Connecting…" />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface">
      <div className="flex flex-col items-center gap-4 text-center p-8">
        {status === 'error' && (
          <>
            <Typography variant="header" size="h2" color="error">
              Failed
            </Typography>
            <Typography variant="body" size="medium" color="bodylighter">
              {errorMsg}
            </Typography>
            <Button variant="secondary" onClick={() => navigate({ to: '/settings/connect' })}>
              Go back
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default OAuthCallback;
